import { useEffect, useRef, useState } from 'react'
import * as XLSX from 'xlsx'
import PropTypes from 'prop-types'
import {
  Button,
  Card,
  SearchSelect,
  SearchSelectItem,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableHead,
  TableHeaderCell,
  TableRow,
  Title,
} from '@tremor/react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid'
import { remapProperties, getDateUTC } from '../helpers'
import "../pages/TableServicesPage.css";
// Estilos necesarios para react-date-range
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { DateRange } from 'react-date-range'
import esLocale from 'date-fns/locale/es'

const ButtonXLS = ({ services = [] }) => {
  if (services.length <= 0) {
    return <></>
  }
  const downloadInfo = () => {
    const servicesData = services.map((s) => ({
      CAAT: s.CAAT,
      integrationNumber: s.integrationNumber,
      client: s.client?.name || '',
      buyer: s.buyer?.name || '',
      invoiceNumber: s.invoiceNumber,
      economicCode: s.economicCode,
      feet: s.feet,
      alphaCode: s.alphaCode,
      plateNumber: s.plateNumber,
      requirementNumber: s.requirementNumber || '',
      remesas: s.remesas || '',
      travelNumber: s.travelNumber || '',
      moduleStatus: s.moduleStatus || 'SIN MÓDULO',
      phytosanitaryPermission: s.phytosanitaryPermission ? (s.phytosanitaryValue ? `Activo: ${s.phytosanitaryValue}` : 'Activo') : 'Inactivo',
      basculePermission: s.basculePermission ? (s.basculeValue ? `Activo: ${s.basculeValue}` : 'Activo') : 'Inactivo',
      overweightPermission: s.overweightPermission ? (s.overweightValue ? `Activo: ${s.overweightValue}` : 'Activo') : 'Inactivo',
      creationDate: `${new Date(s.creationDate).getFullYear()}/${new Date(
        s.creationDate,
      ).getMonth()}/${new Date(s.creationDate).getDate()}`,
    }))

    const servicesInXLSX = servicesData.map(remapProperties)

    const worksheet = XLSX.utils.json_to_sheet(servicesInXLSX)

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data')

    XLSX.writeFile(workbook, 'servicios.xlsx')
  }

  return (
    <Button className="ml-3" variant="primary" onClick={downloadInfo}>
      Descargar XLS
    </Button>
  )
}

// SelectDate ahora usa react-date-range para seleccionar un rango de fechas.
const SelectDate = ({ onValueChange, defaultValue }) => {
  // defaultValue puede ser: Date, string YYYY-MM-DD, o un objeto { startDate, endDate }
  const normalizeRange = (val) => {
    const toDate = (v) => {
      if (!v) return undefined
      if (v instanceof Date) return v
      if (typeof v === 'string') {
        const [y, m, d] = v.split('-').map(Number)
        return new Date(y, m - 1, d)
      }
      return v
    }
    if (val && typeof val === 'object' && 'startDate' in val && 'endDate' in val) {
      return {
        startDate: toDate(val.startDate) || new Date(),
        endDate: toDate(val.endDate) || new Date(),
        key: 'selection',
      }
    }
    const d = toDate(val) || new Date()
    return { startDate: d, endDate: d, key: 'selection' }
  }

  const [range, setRange] = useState(normalizeRange(defaultValue))
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)

  // Sincronizar si cambia defaultValue externamente
  useEffect(() => {
    setRange(normalizeRange(defaultValue))
  }, [defaultValue])

  // Cerrar al hacer click fuera
  useEffect(() => {
    if (!isOpen) return
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [isOpen])

  const handleChange = (ranges) => {
    const sel = ranges.selection
    setRange(sel)
    onValueChange?.({ startDate: sel.startDate, endDate: sel.endDate })
  }

  const formatLabel = (r) => {
    const pad = (n) => `${n}`.padStart(2, '0')
    const s = r.startDate
    const e = r.endDate
    const sLbl = `${pad(s.getDate())}/${pad(s.getMonth() + 1)}/${s.getFullYear()}`
    const eLbl = `${pad(e.getDate())}/${pad(e.getMonth() + 1)}/${e.getFullYear()}`
    return `${sLbl} - ${eLbl}`
  }

  return (
    <div className="ml-3 relative" ref={containerRef}>
      <h1 className="m-0">Rango de fechas</h1>
      <Button variant="secondary" onClick={() => setIsOpen((v) => !v)}>
        {formatLabel(range)}
      </Button>
      {isOpen && (
        <div className="absolute left-0 mt-2 bg-white shadow-lg rounded p-2 z-50 max-w-fit">
          <DateRange
            ranges={[range]}
            onChange={handleChange}
            showSelectionPreview={true}
            moveRangeOnFirstSelection={false}
            months={2}
            direction="horizontal"
            rangeColors={["#2563eb"]}
            locale={esLocale}
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button size="sm" variant="secondary" onClick={() => setIsOpen(false)}>Cerrar</Button>
          </div>
        </div>
      )}
    </div>
  )
}

const SelectStatus = ({ onValueChange, defaultValue = 'ALL' }) => {
  return (
    <div className="max-w-sm ml-3">
      <h1 className="m-0">Status</h1>
      <Select
        onChange={onValueChange}
        placeholder="Seleccionar..."
        defaultValue={defaultValue}
      >
        <SelectItem value="ALL">Todos los status</SelectItem>
        <SelectItem value="Ya en frontera ">Ya en frontera</SelectItem>
        <SelectItem value="DESADUANAMIENTO LIBRE">Desaduanamiento
          libre</SelectItem>
        <SelectItem value="SIN MÓDULO">Sin módulo</SelectItem>
        <SelectItem value="ERROR EN NUMERO DE INTERGRACIÓN Y/O SIN MÓDULO">Error
          en numero de intergración y/o sin módulo</SelectItem>
        <SelectItem value="RECONOCIMIENTO ADUANERO CONCLUIDO">Reconocimiento
          aduanero concluido</SelectItem>
        <SelectItem value="RECONOCIMIENTO ADUANERO">Reconocimiento
          aduanero</SelectItem>
        <SelectItem value="EN PROCESO">En proceso</SelectItem>
        <SelectItem value="RECHAZADO / REQUERIDO">Rechazado / Requerido</SelectItem>
      </Select>
    </div>
  )
}

const SelectLogs = ({ onValueChange }) => {
  return (
    <>
      <div className="max-w-sm ">
        <h1 className="m-0">Cantidad de Registros a Mostrar</h1>
        <Select onChange={onValueChange} placeholder="Seleccionar...">
          <SelectItem value="0">Todos</SelectItem>
          <SelectItem value="10">10 Registros</SelectItem>
          <SelectItem value="20">20 Registros</SelectItem>
          <SelectItem value="50">50 Registros</SelectItem>
          <SelectItem value="100">100 Registros</SelectItem>
        </Select>
      </div>
    </>
  )
}

const SearchLogs = ({
  content,
  showMatchContent,
  propNameSearch,
  deleteMatchContent,
  value,
}) => {
  return (
    <SearchSelect
      icon={MagnifyingGlassIcon}
      onValueChange={showMatchContent}
      onKeyUp={deleteMatchContent}
      value={value}
      placeholder="Seleccionar..."
    >
      {content.map((row, index) => (
        <SearchSelectItem key={index} value={row.props[propNameSearch]}>
          {row.props[propNameSearch]}
        </SearchSelectItem>
      ))}
    </SearchSelect>
  )
}

export const TableComponent = ({
  tableContent = [],
  tableHeaders,
  title = '',
  showSelectLogs = false,
  showSearchInput = false,
  showDateSelect = false,
  showStatusSelect = false,
  showButtonXLS = false,
  propNameSearch = 'name',
  triggerFuncDate = () => {},
  setFilters = () => {},
  filters = {
    date: new Date(),
    dateValue: '0',
    status: 'ALL',
  }
}) => {
  const [tableInfo, setTableInfo] = useState([])
  const [valueSearch, setValueSearch] = useState('')

  // Control de cantidad de registros mostrados
  const logsInScreen = (value = 0) => {
    const data = tableContent
    const num = typeof value === 'string' ? Number(value) : value
    if (!Number.isFinite(num) || num <= 0) {
      setTableInfo(data)
      return
    }
    setTableInfo(data.slice(0, num))
  }

  // Mostrar todo lo que envía el backend (sin filtrar por fecha en cliente; el API aplica los filtros)
  useEffect(() => {
    if (!Array.isArray(tableContent)) return
    setTableInfo(tableContent)
  }, [tableContent])

  const dateFilter = async (value = 0, options = {}) => {
    const nowUTC = await getDateUTC();

    const toLocalYmd = (d) => {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    }

    // Rango de fechas: enviar startDate/endDate al API
    if (value && typeof value === 'object' && 'startDate' in value && 'endDate' in value) {
      const start = new Date(value.startDate)
      const end = new Date(value.endDate)
      const startStr = toLocalYmd(start)
      const endStr = toLocalYmd(end)
      const rangeToken = `${startStr}|${endStr}`

      setTableInfo(tableContent)

      if (!options.silent) {
        setFilters(prev => ({
          ...prev,
          date: { startDate: start, endDate: end },
          dateValue: rangeToken,
        }))
        triggerFuncDate(startStr, filters.status, rangeToken)
      }
      return
    }

    // Fecha exacta (YYYY-MM-DD): enviar al API
    if (typeof value === 'string' && value.length === 10) {
      const [y, m, d] = value.split('-').map(Number)
      const dateToFilter = new Date(y, m - 1, d)
      const targetStr = value

      setTableInfo(tableContent)

      if (!options.silent) {
        setFilters(prev => ({
          ...prev,
          date: dateToFilter,
          dateValue: value,
        }))
        triggerFuncDate(targetStr, filters.status, value)
      }
      return
    }

    // Rango relativo (hoy/ayer/semana/mes): enviar fecha calculada al API
    const offset = Number(value) || 0
    const dateToFilter = new Date(
      nowUTC.getFullYear(),
      nowUTC.getMonth(),
      nowUTC.getDate() - offset,
    )
    const dateStr = toLocalYmd(dateToFilter)

    setTableInfo(tableContent)

    if (!options.silent) {
      setFilters(prev => ({
        ...prev,
        date: dateToFilter,
        dateValue: String(value),
      }))
      triggerFuncDate(dateStr, filters.status, String(value))
    }
  }

  const statusFilter = (value = 'RECONOCIMIENTO ADUANERO') => {
    // Normalizar la fecha actual a cadena YYYY-MM-DD en zona negocio para backend
    const tz = 'America/Mexico_City'
    const toTZYmd = (d) => new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d)

    let dateParam = ''
    if (filters?.date && typeof filters.date === 'object' && 'startDate' in filters.date) {
      dateParam = toTZYmd(new Date(filters.date.startDate))
    } else if (filters?.date instanceof Date) {
      dateParam = toTZYmd(filters.date)
    } else if (typeof filters?.date === 'string' && filters.date.length === 10) {
      dateParam = filters.date
    }

    setFilters(prev => ({
      ...prev,
      status: value,
    }))
    triggerFuncDate(dateParam, value, filters.dateValue)
  }

  const showMatchContent = (value = '') => {
    const data = tableContent
    if (value === '') {
      setTableInfo(data)
    }
    const matchElement = data.filter((d) => d.props[propNameSearch] === value)
    setValueSearch(value)
    setTableInfo(matchElement)
  }

  const deleteMatchContent = (test) => {
    const { keyCode } = test
    if (keyCode === 8) {
      setTableInfo(tableContent)
      setValueSearch('')
    }
  }

  return (
    <>
      <div className="my-3 flex justify-end relative z-20">
        {showSelectLogs ? <SelectLogs onValueChange={logsInScreen}/> : <></>}
        {showDateSelect ? (
          <SelectDate
            onValueChange={dateFilter}
            defaultValue={filters.date}
          />
        ) : (
          <></>
        )}
        {showStatusSelect ? (
          <SelectStatus
            onValueChange={statusFilter}
            defaultValue={filters.status}
          />
        ) : (
          <></>
        )}
        <div className="max-w-sm ml-auto space-y-6 flex items-end">
          {showSearchInput ? (
            <SearchLogs
              content={tableContent}
              showMatchContent={showMatchContent}
              propNameSearch={propNameSearch}
              deleteMatchContent={deleteMatchContent}
              value={valueSearch}
            />
          ) : (
            <></>
          )}
          {showButtonXLS ? (
            <ButtonXLS services={tableInfo.map((t) => t.props)}/>
          ) : (
            <></>
          )}
        </div>
      </div>
      <Card>
        <Title>{title}</Title>
        <div className="table-container">
          <Table className="mt-5">
            <TableHead>
              <TableRow>
                {tableHeaders.map((header, index) => (
                  <TableHeaderCell key={index} className="sticky-header">{header}</TableHeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{tableInfo}</TableBody>
          </Table>
        </div>
      </Card>
    </>
  )
}

SelectLogs.propTypes = {
  onValueChange: PropTypes.func,
}

TableComponent.propTypes = {
  showSearchInput: PropTypes.bool,
  showSelectLogs: PropTypes.bool,
  showDateSelect: PropTypes.bool,
  showStatusSelect: PropTypes.bool,
  showButtonXLS: PropTypes.bool,
  tableContent: PropTypes.array,
  tableHeaders: PropTypes.array,
  title: PropTypes.string,
  propNameSearch: PropTypes.string,
  triggerFuncDate: PropTypes.func,
  setFilters: PropTypes.func,
  filters: PropTypes.object,
}

ButtonXLS.propTypes = {
  services: PropTypes.array,
}

SearchLogs.propTypes = {
  content: PropTypes.array,
  propNameSearch: PropTypes.string,
  showMatchContent: PropTypes.func,
  deleteMatchContent: PropTypes.func,
  value: PropTypes.string,
}

SelectDate.propTypes = {
  onValueChange: PropTypes.func,
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.shape({
      startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    })
  ]),
}

SelectStatus.propTypes = {
  onValueChange: PropTypes.func,
  defaultValue: PropTypes.string,
}
